import { Button } from '@/components/ui/button';
import { useCallback, useEffect, useRef, useState } from 'react';

/*
 * The shared OWT app switcher (apps.owt.team), origin-isolated.
 *
 * Sentinel is the security cockpit, so this one matters here more than anywhere:
 * the launcher used to be apps.owt.team's `launcher.js` executing in Sentinel's
 * own origin, with access to the session and the DOM. It is now their
 * `embed.html` inside a sandboxed cross-origin iframe, driven over a closed
 * postMessage verb set. No third-party script runs in this origin any more,
 * which is also why the CSP in public/staticwebapp.config.json keeps
 * apps.owt.team out of script-src.
 *
 * The component's own button used to live in Sentinel's DOM (`position="inline"`)
 * and is now inside the frame, so the trigger below is ours: the same 3x3 dot
 * grid, in the same header slot. The panel, tiles and registry still come from
 * apps.owt.team, so adding an app there needs no change here.
 */

const LAUNCHER_ORIGIN = 'https://apps.owt.team';

export function OwtAppLauncher() {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const readyRef = useRef(false);
  const [open, setOpen] = useState(false);

  // Frame -> host: trust only our frame's exact origin AND window, and only the
  // closed verb set.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== LAUNCHER_ORIGIN) return;
      if (e.source !== frameRef.current?.contentWindow) return;
      const msg = e.data as { type?: unknown } | null;
      if (!msg || typeof msg.type !== 'string') return;
      if (msg.type === 'owt-launcher:ready') { readyRef.current = true; return; }
      if (msg.type === 'owt-launcher:close') { setOpen(false); return; }
      // any other type: ignored
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const toggle = useCallback(() => {
    const frame = frameRef.current;
    if (!frame?.contentWindow || !readyRef.current) return;
    if (open) {
      setOpen(false);
      frame.contentWindow.postMessage({ type: 'owt-launcher:close' }, LAUNCHER_ORIGIN);
      return;
    }
    // Sentinel's trigger sits in the top-RIGHT of the header, unlike the
    // sidebar lockups elsewhere in the estate, so sending the real rect is what
    // keeps the panel under the button instead of over in the far corner.
    const r = btnRef.current?.getBoundingClientRect() ?? { top: 8, left: 8, right: 8, bottom: 8 };
    // The frame's background is transparent, so it cannot sample our theme by
    // luminance — send it.
    const dark = document.documentElement.classList.contains('dark');
    setOpen(true);
    frame.contentWindow.postMessage(
      {
        type: 'owt-launcher:open',
        anchor: { top: r.top, left: r.left, right: r.right, bottom: r.bottom },
        theme: dark ? 'dark' : 'light',
      },
      LAUNCHER_ORIGIN,
    );
  }, [open]);

  return (
    <>
      <Button
        ref={btnRef}
        variant="ghost"
        size="icon"
        aria-label="OWT apps"
        aria-haspopup="true"
        aria-expanded={open}
        title="OWT apps"
        onClick={toggle}
        className="text-muted-foreground hover:text-foreground"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" className="fill-current">
          <circle cx="2" cy="2" r="1.7" /><circle cx="9" cy="2" r="1.7" /><circle cx="16" cy="2" r="1.7" />
          <circle cx="2" cy="9" r="1.7" /><circle cx="9" cy="9" r="1.7" /><circle cx="16" cy="9" r="1.7" />
          <circle cx="2" cy="16" r="1.7" /><circle cx="9" cy="16" r="1.7" /><circle cx="16" cy="16" r="1.7" />
        </svg>
      </Button>
      <iframe
        ref={frameRef}
        id="owt-launcher-frame"
        src={`${LAUNCHER_ORIGIN}/embed.html`}
        title="OWT app launcher"
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        style={{
          position: 'fixed',
          inset: 0,
          width: '100%',
          height: '100%',
          border: 0,
          zIndex: 2147483000,
          pointerEvents: open ? 'auto' : 'none',
          display: open ? 'block' : 'none',
        }}
      />
    </>
  );
}
